import { useState, useCallback, FC, useEffect } from "react";
import EmojiPicker from "emoji-picker-react";
import { Query_Imgix } from "./../../../../api/imgix/index";
import QueryDB_Notifications from "./../../../../api/queryDB/notifications/index";
import { createToast } from "@/utils/toaster/createToast";
import QueryDB from "@/api/queryDB/common";
import EmojiWrapper from "@/components/EmojiWrapper/EmojiWrapper";
import ImagePreview from "@/components/ImagePreview/ImagePreview";
import { ImageUpload } from "@/components/ImageUpload/ImageUpload";
import { Profile } from "@/models/database/profiles/profile.model";
import { useCurrentUserProfile } from "@/store/auth/userProfile.store";
import { timestamp } from "@/api/firebase/firebase.config";
import { v4 as uuid } from "uuid";
import { notificationsTextMapping } from "@/utils/constants/notificationsText";
import {
  Avatar,
  Box,
  Button,
  Spinner,
  TextField,
  Grid,
  Card,
} from "@radix-ui/themes";
import { ChevronRightIcon, FaceIcon, ImageIcon } from "@radix-ui/react-icons";
export type CreatePostInitialValuesProps = {
  postText: string;
  postMedia?: File;
};

const initialValues: CreatePostInitialValuesProps = {
  postText: "",
  postMedia: null,
};

export const CreatePostForm: FC<{
  postedIn: "feed" | "team" | "news";
  currentUser: Profile;
  communityData: GetUserCommunityBasedOnSlug;
  setFeedUpdateTrigger: any;
  disabledInput?: boolean;
  setSinglePost?: (data: any) => void;
  parentId?: string;
  isPreview?: boolean;
  inputPlaceholder?: string;
}> = ({
  postedIn,
  currentUser,
  communityData,
  setFeedUpdateTrigger,
  disabledInput,
  setSinglePost,
  parentId,
  isPreview = false,
  inputPlaceholder,
}): JSX.Element => {
  const [values, setValues] =
    useState<CreatePostInitialValuesProps>(initialValues);
  const [show, setShow] = useState(false);
  const [submitting, isSubmitting] = useState<boolean>(false);
  const [lastClickedEmoji, setLastClickedEmoji] = useState<string>("");
  const currUserProfile = useCurrentUserProfile((s) => s.user);

  const setFieldValue = (targetName: string, value: any) => {
    setValues((prev) => ({
      ...prev,
      [targetName]: value,
    }));
  };

  useEffect(() => {
    if (lastClickedEmoji) {
      setFieldValue("postText", `${values.postText}${lastClickedEmoji}`);
      setLastClickedEmoji(undefined);
    }
  }, [lastClickedEmoji]);

  const submit = useCallback(
    async (values: CreatePostInitialValuesProps) => {
      if (!currentUser.uid || isPreview) {
        window.location.reload();
        return;
      }

      if (!values.postMedia && !values.postText) {
        return createToast({
          type: "info",
          message: "Message or picture is required",
        });
      }

      isSubmitting(true);
      let firebaseFileURL: string | null = null;

      if (values?.postMedia?.name) {
        firebaseFileURL = await Query_Imgix.convertImageToImgIX(
          values.postMedia,
          `${postedIn}/${parentId}/${currentUser.slug}`
        );
      }
      const newPost = {
        postText: values.postText as string,
        postPhoto:
          values.postMedia && values.postMedia.type.includes("image")
            ? firebaseFileURL
            : null,
        postVideo:
          values.postMedia && values.postMedia.type.includes("video")
            ? firebaseFileURL
            : null,
        uid: currentUser.uid as string,
        wasEdited: false,
        totalSharesNumber: 0,
        postedIn: postedIn,
        type: "userPost",
        parentId: parentId || null,
      };

      const id = uuid();

      await QueryDB.createNewDoc("posts", newPost, id)
        .then(async () => {
          const existingUsers = Array.isArray(communityData?.friends.users)
            ? communityData?.friends.users.map((friend: any) => ({
                username: `@${friend.userName}`,
                slug: friend.slug,
                uid: friend.uid,
              }))
            : [];

          // const notificationsTextMapping = {
          //   "post-mention": "mentioned you in a post",
          // };

          setSinglePost({
            ...newPost,
            additionalData: {},
            authorLogo: currUserProfile.profileLogoUrl,
            authorSlug: currUserProfile.slug,
            authorUsername: currUserProfile.userName,
            created: timestamp.now(),
            fullName: currUserProfile.fullName,
            hasCurrentUserLikedThisPost: false,
            popularity: 0,
            totalCommentsNumber: 0,
            totalLikesNumber: 0,
            visibility: true,
            id,
          });

          const mentionedUsers = values.postText
            .split(" ")
            .filter((word: string) => word.startsWith("@"));

          const uniqueMentionedUsers = new Set(mentionedUsers);

          uniqueMentionedUsers.forEach((mentionedUsername) => {
            const mentionedUser = existingUsers.find(
              (user) => user.username === mentionedUsername
            );
            const isCurrentUser = mentionedUser.uid === currUserProfile.uid;
            if (mentionedUser && !isCurrentUser) {
              QueryDB_Notifications.createNewNotification(
                currentUser.uid,
                mentionedUser.uid,
                id,
                `post-mention-${postedIn}`,
                `${notificationsTextMapping[`post-mention`]}`
              );
            }
          });
        })
        .then(() => {
          createToast({
            type: "success",
            message: `Posted in ${postedIn}`,
          });
        })
        .catch((err) => {
          isSubmitting(false);
          createToast({
            type: "success",
            message: "Failed to upload post",
          });
        });
      isSubmitting(false);
      setValues(initialValues);
    },
    [currentUser]
  );

  return (
    <Box>
      <form
        className="form"
        autoComplete="off"
        onSubmit={(e) => e.preventDefault()}
      >
        <>
          <Box position="relative">
            <Card>
              <Grid gap="3" columns="12">
                {/* Avatar */}
                <Box
                  gridColumnStart={{ initial: "1", md: "1" }}
                  gridColumnEnd={{ initial: "3", md: "2" }}
                >
                  <Avatar
                    src={currentUser.profileLogoUrl}
                    fallback={currentUser.userName.slice(0, 1)}
                    radius="full"
                    size="3"
                  />
                </Box>

                {/* Input Field */}
                <Box
                  gridColumnStart={{ initial: "3", md: "2" }}
                  gridColumnEnd={{ initial: "11", md: "12" }}
                >
                  <TextField.Root
                    placeholder={
                      inputPlaceholder ||
                      "Write something you would like to share"
                    }
                    size="3"
                    type="text"
                    value={values.postText}
                    onChange={(e) => {
                      setFieldValue("postText", e.target.value);
                    }}
                    disabled={disabledInput}
                  >
                    {/* File button */}
                    <TextField.Slot>
                      <ImageUpload
                        isDisabled={isPreview || disabledInput}
                        // acceptType="image/*, video/*" // Currently disabled as there is no NSFW filter on videos. TODO for later version
                        acceptType="image/*"
                        setIsLoading={() => {}}
                        setFieldValue={setFieldValue}
                        setType={() => {}}
                        value={values.postMedia}
                        name="postMedia"
                      >
                        <ImageIcon color="gray" />
                      </ImageUpload>
                    </TextField.Slot>

                    {/* Emoji button */}
                    <TextField.Slot>
                      <Button
                        variant="ghost"
                        style={{ width: "100%" }}
                        onClick={() => setShow((pre) => !pre)}
                      >
                        <FaceIcon color="gray" />
                      </Button>
                    </TextField.Slot>
                  </TextField.Root>
                </Box>

                {/* Submit button */}
                <Box
                  gridColumnStart={{ initial: "11", md: "12" }}
                  gridColumnEnd={{ initial: "13", md: "13" }}
                >
                  <Button
                    variant="solid"
                    onClick={() => {
                      if (!submitting) {
                        submit(values);
                      }
                    }}
                    radius="full"
                    style={{ height: "100%" }}
                    disabled={
                      (values.postText.length <= 0 && !values.postMedia) ||
                      submitting
                    }
                  >
                    {values.postText.length <= 0 ||
                    !values.postMedia ||
                    (values.postText.length > 0 && values.postMedia) ? (
                      submitting ? (
                        <Spinner />
                      ) : (
                        <ChevronRightIcon />
                      )
                    ) : (
                      !submitting && <ChevronRightIcon />
                    )}
                  </Button>
                </Box>
              </Grid>
            </Card>

            {/* Emoji picker */}
            <EmojiWrapper show={show} setShow={setShow}>
              <Box position="absolute" inset="100% 0 auto auto">
                <EmojiPicker
                  emojiVersion="0.6"
                  lazyLoadEmojis={true}
                  onEmojiClick={(emojiObject) => {
                    if (!disabledInput && !isPreview) {
                      setLastClickedEmoji(emojiObject.emoji);
                    }
                  }}
                />
              </Box>
            </EmojiWrapper>
          </Box>
        </>

        {!isPreview && !disabledInput && values.postMedia && (
          <ImagePreview
            funcToRemove={() => {
              setFieldValue("postMedia", undefined);
            }}
            file={values.postMedia}
          />
        )}
      </form>
    </Box>
  );
};
